import Chip, { chipClasses } from "@mui/material/Chip";
import styled from "@emotion/styled";
import { Card } from "@mui/material";

export interface LoadingSkeletonProps {
	isLoading?: boolean;
}

export const GridWrapper = styled.div(({ onClick, theme }) => ({
	...(onClick && {
		cursor: "pointer",
		[`${LineItemBackground}`]: {
			"&:hover": {
				backgroundColor: theme.project.colors.backgroundColors["card:hover"]
			}
		}
	})
}));

export const PricingContainer = styled.div(({ theme }) => ({
	"&&&&&": {
		[theme.breakpoints.down("md")]: {
			...theme.project.typography.subtitle.subtitle2,
			color: theme.project.colors.fontColors.main,
			display: "flex",
			justifyContent: "space-between",
			width: "100%"
		}
	}
}));

export const StandardPricingContainer = styled(PricingContainer)(({ theme }) => ({
	[theme.breakpoints.down("md")]: {
		...theme.project.typography.subtitle.subtitle2,
		color: theme.project.colors.fontColors.main,
		"div:nth-of-type(2)": {
			...theme.project.typography.body.body2,
			color: theme.project.colors.fontColors.secondary
		}
	}
}));

export const FinalPriceContainerMobile = styled.div(({ theme }) => ({
	"&&&&&": {
		[theme.breakpoints.down("md")]: {
			...theme.project.typography.subtitle.subtitle2,
			display: "flex",
			justifyContent: "space-between",
			width: "100%",
			div: {
				color: theme.project.colors.primaryColors.main
			}
		}
	}
}));

export const PromotionsValue = styled.div(({ theme }) => ({
	...theme.project.typography.body.body2,
	gridArea: "4 / 7",
	[theme.breakpoints.down("md")]: {
		gridArea: "auto"
	}
}));

export const FinalPriceContainer = styled.div(({ theme }) => ({
	"&&&&&": {
		...theme.project.typography.subtitle.subtitle2,
		color: theme.project.colors.primaryColors.main,
		width: "max-content",
		gridArea: "5 / 6",
		display: "flex",
		[theme.breakpoints.down("md")]: {
			gridArea: "auto"
		}
	}
}));

export const FinalPriceValue = styled.div(({ theme }) => ({
	...theme.project.typography.body.body2,
	color: theme.project.colors.primaryColors.main,
	gridArea: "5 / 7",
	[theme.breakpoints.down("md")]: {
		gridArea: "auto"
	}
}));

export const PromoTextDesktop = styled.div(({ theme }) => ({
	[theme.breakpoints.down("md")]: {
		display: "none"
	}
}));

export const AdjustedListPrice = styled.div(({ theme }) => ({
	...theme.project.typography.subtitle.subtitle2,
	lineHeight: 0,
	gridArea: "2 / 6",
	width: "max-content"
}));

export const AdjustedListPriceValue = styled.div(({ theme }) => ({
	...theme.project.typography.body.body2,
	gridArea: "3 / 6",
	width: "max-content"
}));

export const ExtNetPriceContainer = styled.div(({ theme }) => ({
	...theme.project.typography.subtitle.subtitle2,
	lineHeight: 0,
	gridArea: "2 / 7",
	width: "max-content"
}));

export const ExtNetPriceValue = styled.div(({ theme }) => ({
	...theme.project.typography.body.body2,
	gridArea: "3 / 7",
	width: "max-content"
}));

export const PromotionPriceContainer = styled.div(({ theme }) => ({
	...theme.project.typography.subtitle.subtitle2,
	gridArea: "4 / 6",
	width: "max-content"
}));

export const LineItemBackground = styled(Card, {
	shouldForwardProp: (prop) => prop !== "isLoading"
})<LoadingSkeletonProps>(({ isLoading, theme }) => ({
	display: isLoading ? "none" : "grid",
	gap: 16,
	backgroundColor: theme.project.colors.backgroundColors.card,
	borderRadius: "unset",
	padding: 16
}));

export const AddOnAccountBillTo = styled.div({
	marginTop: "16px"
});

export const LineItemAccordionDetails = styled.div({
	display: "flex",
	flexDirection: "column",
	gap: 8,
	padding: 16
});

const FIRST_ROW = "& > div:nth-of-type(1)";
const FIRST_ROW_DETAILS_COLUMNS = "& > div:nth-of-type(n + 2)";
const getFirstRowModificationsSelector = (columns: number) => `& > div:nth-of-type(n + ${columns + 1})`;
const MODIFICATION_ROWS = FIRST_ROW_DETAILS_COLUMNS;
export const SUBGRID_TEMPLATE = {
	display: "grid",
	gridColumn: "1 / -1",
	gridTemplateColumns: "subgrid"
};

interface LineItemGridContainerProps {
	mainContentColumnCount: number;
	isNewOrderBuildOrderPage?: boolean;
	isSpecialModification?: boolean;
}
export const LineItemGridContainer = styled.div<LineItemGridContainerProps>(
	({ theme, mainContentColumnCount, isNewOrderBuildOrderPage, isSpecialModification }) =>
		!isSpecialModification
			? {
					color: theme.project.colors.fontColors.secondary,
					display: "grid",
					gridTemplateColumns: "repeat(8, 1fr)",
					gap: 24,
					[FIRST_ROW]: {
						...SUBGRID_TEMPLATE,
						[FIRST_ROW_DETAILS_COLUMNS]: {
							color: theme.project.colors.fontColors.main,
							fontSize: "var(--font-size-3)",
							fontWeight: 500,
							lineHeight: "var(--line-height-8)",
							...(mainContentColumnCount > 0 && { gridColumn: "span 1" }),
							div: {
								color: theme.project.colors.fontColors.secondary,
								fontWeight: 400,
								fontSize: "var(--font-size-3)",
								"&:nth-of-type(1)": {
									color: theme.project.colors.fontColors.main,
									fontWeight: 500
								}
							}
						},
						...(mainContentColumnCount > 0 && {
							[getFirstRowModificationsSelector(mainContentColumnCount)]: {
								display: "none" // Swap with the copy outside the first row
							}
						})
					},
					[MODIFICATION_ROWS]: {
						// Set up divs after the first row container to all be subgrid
						...SUBGRID_TEMPLATE,
						rowGap: 8
					},
					[theme.breakpoints.down("md")]: {
						gridTemplateColumns: "1fr",
						[FIRST_ROW]: {
							rowGap: 8,
							[FIRST_ROW_DETAILS_COLUMNS]: {
								display: "flex",
								gap: 16,
								[`&:nth-of-type(${mainContentColumnCount})`]: {
									alignItems: "center",
									flexDirection: isNewOrderBuildOrderPage ? "column" : "row"
								}
							},
							...(mainContentColumnCount > 0 && {
								[getFirstRowModificationsSelector(mainContentColumnCount)]: {
									flexDirection: "column",
									gap: 8,
									gridRow: 2
								}
							})
						}
					}
				}
			: {
					"& > div": {
						display: "flex",
						flexDirection: "column",
						gap: 4
					}
				}
);

export const ModContainer = styled.div<{ row: number }>(({ row }) => ({
	"&&&&": {
		gridRow: row
	}
}));

export const LineItemSkeletonContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "isLoading"
})<LoadingSkeletonProps>(({ isLoading }) => ({
	display: isLoading ? "grid" : "none",
	gridTemplateColumns: "5fr 1fr 1fr 1fr",
	gap: "16px"
}));

export const LineItemLeftSkeletons = styled.div({
	gridTemplateRows: "2fr"
});

export const LineItemRightSkeletons = styled.div({
	gridTemplateColumns: "repeat(3, 1fr)"
});

export const LineItemErrorMessageArr = styled.div(({ theme }) => ({
	display: "flex",
	flexDirection: "column"
}));

export const LineItemErrorMessage = styled.div(({ theme }) => ({
	...theme.project.typography.subtitle.subtitle2,
	display: "flex",
	alignItems: "center",
	color: "var(--red-700)",
	gap: "8px",
	svg: {
		width: 16,
		height: 16,
		margin: 0
	}
}));

export const EditedChip = styled(Chip)(({ theme }) => ({
	backgroundColor: theme.project.colors.allColors.hex.blue.light.lightBlue50,
	color: theme.project.colors.primaryColors.main,
	width: "82px",
	[`& .${chipClasses.icon}`]: {
		color: theme.project.colors.primaryColors.main,
		width: 16,
		height: 16,
		margin: "0px"
	},
	span: {
		padding: "0px 6px",
		fontWeight: 500,
		marginTop: "2px"
	}
}));
